'use client';

import clsx from 'clsx';
import type { ForwardedRef } from 'react';
import { Button as RACButton } from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { InternalSpinner } from '../internal/spinner/spinner.js';
import { SPINNER_SIZES } from './constants.js';
import type { ActionButtonProps, BaseButtonProps, TextButtonProps } from './types.js';

export interface ButtonProps extends BaseButtonProps, TextButtonProps, ActionButtonProps {}

function Button(
  {
    children,
    variant = 'secondary',
    tone = 'base',
    size = 'medium',
    iconStart,
    iconEnd,
    isLoading,
    loadingLabel,
    isDisabled,
    fullWidth,
    UNSAFE_className,
    UNSAFE_style,
    ...props
  }: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  useProductionWarning(() => {
    if (isLoading && !loadingLabel) {
      console.warn('Button requires loadingLabel when isLoading is true for accessibility');
    }
  }, [isLoading, loadingLabel]);

  return (
    <RACButton
      {...props}
      ref={ref}
      className={clsx('cim-button', UNSAFE_className)}
      style={UNSAFE_style}
      data-variant={variant}
      data-tone={tone}
      data-size={size}
      data-full-width={fullWidth || undefined}
      isDisabled={isDisabled}
      isPending={isLoading}
    >
      {({ isPending }) => (
        <>
          {iconStart}
          <span className="cim-button-text">{children}</span>
          {iconEnd}
          {isPending && <InternalSpinner size={SPINNER_SIZES[size]} aria-label={loadingLabel} />}
        </>
      )}
    </RACButton>
  );
}

/**
 * Displays a labelled button that allows users to perform an action.
 *
 * See [button usage guidelines](https://ui.cimpress.io/components/button/).
 */
const _Button = withStyleProps(forwardRef(Button), 'Button');

export { _Button as Button };
